import {
  DropdownMenu,
  FormControlSizes,
  IconButton,
  IconName,
  MixpanelEvent,
  MixpanelEventProperty,
  useMixpanel,
  type MixpanelEventSource,
  type Security,
} from '@talos/kyoko';
import { memo, useCallback } from 'react';
import type { MultilegComboType } from '../MultilegCombo/enums';
import { CloneSymbolDropdownItem } from './CloneSymbolDropdownItem';
import { CreateMultilegDropdownItem } from './CreateMultilegDropdownItem';
import { FavoriteSymbolDropdownItem } from './FavoriteSymbolDropdownItem';

interface SymbolActionsMenuProps {
  security: Security | undefined;
  mixpanelSource: MixpanelEventSource;
  onCreateMultileg?: (multilegType: MultilegComboType) => void;
  onCloneSymbol?: (security: Security) => void;
  dataTestId?: string;
  /** Renders the dropdown in a portal so it is not clipped by an overflow-hidden ancestor, e.g. a grid cell. */
  usePortal?: boolean;
  ariaLabel?: string;
}

export const SymbolActionsMenu = memo(function SymbolActionsMenu({
  security,
  mixpanelSource,
  onCreateMultileg,
  onCloneSymbol,
  dataTestId = 'symbol-actions-menu',
  usePortal,
  ariaLabel = 'Open symbol menu',
}: SymbolActionsMenuProps) {
  const mixpanel = useMixpanel();

  const handleMenuOpen = useCallback(() => {
    mixpanel.track(MixpanelEvent.OpenSymbolMenu, {
      [MixpanelEventProperty.Source]: mixpanelSource,
    });
  }, [mixpanel, mixpanelSource]);

  return (
    <DropdownMenu
      onOpen={handleMenuOpen}
      usePortal={usePortal}
      triggerElement={
        <IconButton
          data-testid={dataTestId}
          ghost
          size={FormControlSizes.Small}
          dim
          icon={IconName.DotsVertical}
          aria-label={ariaLabel}
        />
      }
      trigger="click"
      placement="bottom-end"
      dropdownProps={{ style: { width: '220px' } }}
    >
      <FavoriteSymbolDropdownItem security={security} source={mixpanelSource} />
      <CloneSymbolDropdownItem security={security} onCloneSymbol={onCloneSymbol} />
      <CreateMultilegDropdownItem security={security} onCreateMultileg={onCreateMultileg} />
    </DropdownMenu>
  );
});
